// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type {
  AnswerMap,
  WizardEnumOption,
  WizardField,
  WizardGate,
  WizardGroup,
  WizardSection,
} from './types';

/**
 * Pure helpers for the Fact Find wizard renderer. The visibility semantics
 * mirror the Resource Server's ProfileFormSectionValidator (gates with
 * reveals/exclusive, enum followUps, showIf) so callers only submit what the
 * server will accept. See docs/specs/fact-find-wizard-prd.md §4.
 */

export type { AnswerMap } from './types';

export function optionValue(opt: string | WizardEnumOption): string {
  return typeof opt === 'string' ? opt : opt.value;
}

export function optionLabel(opt: string | WizardEnumOption): string {
  return typeof opt === 'string' ? opt : (opt.label ?? opt.value);
}

export interface GateState {
  /** Gated content visible (boolean gate true / multiselect non-empty / no gate). */
  open: boolean;
  /** Group/field ids revealed by the selected multiselect options ('*' = all). */
  revealed: Set<string>;
}

/** Evaluate a (section or group) gate against the section's answers. */
export function gateState(gate: WizardGate | undefined, answers: AnswerMap): GateState {
  if (!gate) {
    return { open: true, revealed: new Set(['*']) };
  }
  const value = answers[gate.id];
  if (gate.type === 'boolean') {
    return value === true
      ? { open: true, revealed: new Set(['*']) }
      : { open: false, revealed: new Set() };
  }
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const revealed = new Set<string>();
  for (const opt of gate.options ?? []) {
    if (selected.includes(opt.value)) {
      for (const r of opt.reveals ?? []) revealed.add(r);
    }
  }
  return { open: selected.length > 0, revealed };
}

/**
 * Whether a (section or group) gate has been explicitly answered — a boolean gate
 * needs Yes or No, a multiselect gate needs at least one selection. A mandatory
 * yes/no left unanswered keeps its section/group incomplete (the page can't be
 * marked done until it's answered), even though "No"/a closed gate then demands
 * nothing further. No gate ⇒ trivially answered.
 */
export function isGateAnswered(gate: WizardGate | undefined, answers: AnswerMap): boolean {
  if (!gate) return true;
  const value = answers[gate.id];
  if (gate.type === 'boolean') return typeof value === 'boolean';
  return Array.isArray(value) && value.length > 0;
}

/** Toggle a multiselect gate option, honouring `exclusive` ("None of these"). */
export function toggleGateOption(
  gate: WizardGate,
  current: string[],
  value: string,
): string[] {
  const opt = (gate.options ?? []).find((o) => o.value === value);
  if (current.includes(value)) {
    return current.filter((v) => v !== value);
  }
  if (opt?.exclusive) {
    return [value];
  }
  const exclusives = new Set(
    (gate.options ?? []).filter((o) => o.exclusive).map((o) => o.value),
  );
  return [...current.filter((v) => !exclusives.has(v)), value];
}

/** Field ids that are followUps targets of any enum option in this field list. */
function followUpTargets(fields: WizardField[]): Set<string> {
  const targets = new Set<string>();
  for (const f of fields) {
    for (const opt of f.options ?? []) {
      if (typeof opt !== 'string') {
        for (const t of opt.followUps ?? []) targets.add(t);
      }
    }
  }
  return targets;
}

/** Field ids revealed by the currently-selected enum options. */
function activeFollowUps(fields: WizardField[], answers: AnswerMap): Set<string> {
  const active = new Set<string>();
  for (const f of fields) {
    const selected = answers[f.id];
    if (selected == null) continue;
    for (const opt of f.options ?? []) {
      if (typeof opt !== 'string' && opt.value === String(selected)) {
        for (const t of opt.followUps ?? []) active.add(t);
      }
    }
  }
  return active;
}

/**
 * Whether a field's — or a (sub-)group's — `showIf` condition holds against
 * the given answer scope. Structural on `{ showIf? }` so the same check
 * covers both `WizardField.showIf` and `WizardGroup.showIf` (a nested
 * sub-group gated on a value in its parent item's own answers).
 */
export function showIfSatisfied(field: { showIf?: WizardField['showIf'] }, answers: AnswerMap): boolean {
  const cond = field.showIf;
  if (!cond) return true;
  const actual = answers[cond.field];
  if ('equals' in cond && cond.equals !== undefined) {
    return actual != null && String(actual) === String(cond.equals);
  }
  if ('notEquals' in cond && cond.notEquals !== undefined) {
    return actual == null || String(actual) !== String(cond.notEquals);
  }
  if (cond.in) {
    return actual != null && cond.in.map(String).includes(String(actual));
  }
  return true;
}

/**
 * The visible subset of a field list given the current answers in the same
 * scope (group item / per-client block / section fields) and the reveal set
 * from a multiselect section gate.
 *
 * `soloClientId` (client wizard only, set when the relationship has a single
 * client) additionally drops every `clientRef` "who does this belong to" owner
 * picker — a solo relationship has exactly one possible owner, so the pickers are
 * hidden and the sole client is auto-seeded instead (see {@link soloOwnerSeed}).
 * Omitted (advisor accordion, joint relationships, the gate/prune call-sites) →
 * clientRef fields stay visible, unchanged behaviour.
 */
export function visibleFields(
  fields: WizardField[],
  answers: AnswerMap,
  revealed: Set<string>,
  soloClientId?: string,
): WizardField[] {
  const targets = followUpTargets(fields);
  const active = activeFollowUps(fields, answers);
  return fields.filter((f) => {
    if (!revealed.has('*') && !revealed.has(f.id)) return false;
    if (targets.has(f.id) && !active.has(f.id)) return false;
    if (soloClientId && f.type === 'clientRef') return false;
    return showIfSatisfied(f, answers);
  });
}

/**
 * Apply a field change within one answer scope, then drop the value of every
 * field the change now hides — so a stale `followUp`/`showIf` value can't linger
 * (re-selecting a Dependant's "Life stage" must reset the fee it used to reveal)
 * nor keep a downstream field revealed through a now-hidden control. Iterates to
 * a fixed point because clearing one field can hide the next (Life stage → the
 * "Plans …?" question → "How much will fees cost"). Only fields in `fields` are
 * pruned; every other key (nested groups, gates, reserved `_`-prefixed refs) is
 * left untouched, and the changed field itself is never dropped.
 */
export function applyFieldChange(
  fields: WizardField[],
  scopeAnswers: AnswerMap,
  fieldId: string,
  value: unknown,
  revealed: Set<string> = new Set(['*']),
): AnswerMap {
  let next: AnswerMap = { ...scopeAnswers, [fieldId]: value };
  for (;;) {
    const visibleIds = new Set(visibleFields(fields, next, revealed).map((f) => f.id));
    const stale = fields.filter(
      (f) => f.id !== fieldId && f.id in next && next[f.id] !== undefined && !visibleIds.has(f.id),
    );
    if (stale.length === 0) return next;
    next = { ...next };
    for (const f of stale) delete next[f.id];
  }
}

/**
 * Defaults to seed for an answer scope: each currently-visible field that
 * declares a `default` but has no value yet. Returns only the missing pairs (an
 * empty object when there is nothing to seed), so callers can no-op cheaply.
 * Scoping to visible fields keeps a default (e.g. Currency → "EUR") from
 * polluting items whose selection never reveals that field.
 */
export function pendingDefaults(
  fields: WizardField[],
  answers: AnswerMap,
  revealed: Set<string>,
): AnswerMap {
  const patch: AnswerMap = {};
  for (const f of visibleFields(fields, answers, revealed)) {
    if (f.default !== undefined && answers[f.id] === undefined) {
      patch[f.id] = f.default;
    }
  }
  return patch;
}

/** Whether a group renders, given the section gate state. */
export function isGroupRevealed(group: WizardGroup, section: GateState): boolean {
  return section.open && (section.revealed.has('*') || section.revealed.has(group.id));
}

/**
 * Solo relationships (one client) hide the `clientRef` "who does this belong to"
 * owner pickers ({@link visibleFields} with `soloClientId`). This seeds the sole
 * client's id into every schema-visible `clientRef` whose value isn't already that
 * client — so the (required) owner is present for the save gate, the backend
 * validator and the financial-position projectors (none of which have a
 * single-client fallback). It walks the section exactly like {@link isSectionComplete}
 * (section-level fields, then each revealed group's repeating items / non-repeating
 * object), only touching *visible* clientRefs (so e.g. `homeOwnership`, behind the
 * "Own home" branch, is untouched while renting) and normalising any stale value
 * (a leftover `"JOINT"` or other-client id) to the sole client.
 *
 * Returns a NEW answers object, or `null` when nothing needed seeding — so the
 * caller's mount effect no-ops and converges without a render loop. PER_CLIENT
 * sections (Personal Details) carry no clientRef owner fields, so they are skipped.
 */
export function soloOwnerSeed(
  section: WizardSection,
  answers: AnswerMap,
  soloClientId: string,
): AnswerMap | null {
  if (section.layout === 'PER_CLIENT') return null;
  const result: AnswerMap = { ...answers };
  let changed = false;

  const sectionGate = gateState(section.gate, answers);

  // Section-level clientRef fields live at the top level of the answers object.
  if (sectionGate.open || !section.gate) {
    for (const f of visibleFields(section.fields ?? [], answers, sectionGate.revealed)) {
      if (f.type === 'clientRef' && result[f.id] !== soloClientId) {
        result[f.id] = soloClientId;
        changed = true;
      }
    }
  }

  // Group clientRef fields are nested under answers[groupId] (an item array for a
  // repeating group, a single object otherwise).
  for (const group of section.groups ?? []) {
    if (!isGroupRevealed(group, sectionGate) && !group.alwaysVisible) continue;
    const groupGate = gateState(group.gate, answers);
    if (group.gate && !groupGate.open) continue;

    if (group.repeating) {
      if (!Array.isArray(answers[group.id])) continue;
      const items = answers[group.id] as AnswerMap[];
      let itemsChanged = false;
      const nextItems = items.map((item) => {
        let nextItem = item;
        for (const f of visibleFields(group.fields, item, new Set(['*']))) {
          if (f.type === 'clientRef' && nextItem[f.id] !== soloClientId) {
            if (nextItem === item) nextItem = { ...item };
            nextItem[f.id] = soloClientId;
          }
        }
        if (nextItem !== item) itemsChanged = true;
        return nextItem;
      });
      if (itemsChanged) {
        result[group.id] = nextItems;
        changed = true;
      }
    } else {
      const groupAnswers = (answers[group.id] as AnswerMap) ?? {};
      let nextGroup = groupAnswers;
      for (const f of visibleFields(group.fields, groupAnswers, new Set(['*']))) {
        if (f.type === 'clientRef' && nextGroup[f.id] !== soloClientId) {
          if (nextGroup === groupAnswers) nextGroup = { ...groupAnswers };
          nextGroup[f.id] = soloClientId;
        }
      }
      if (nextGroup !== groupAnswers) {
        result[group.id] = nextGroup;
        changed = true;
      }
    }
  }

  return changed ? result : null;
}

/**
 * Recursively prune empty values before saving: drop undefined / null / '' /
 * empty objects / empty arrays, keep `false` and `0`. Mirrors the legacy
 * form's buildAnswers behaviour so optional fields stay absent.
 */
export function cleanAnswers(value: unknown): unknown {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  if (Array.isArray(value)) {
    const items = value.map(cleanAnswers).filter((v) => v !== undefined);
    return items.length === 0 ? undefined : items;
  }
  if (typeof value === 'object') {
    const out: AnswerMap = {};
    for (const [k, v] of Object.entries(value as AnswerMap)) {
      const cleaned = cleanAnswers(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return Object.keys(out).length === 0 ? undefined : out;
  }
  return value; // numbers (incl. 0) and booleans (incl. false)
}

/** Clean a whole section's answers, always returning an object. */
export function cleanSectionAnswers(answers: AnswerMap): AnswerMap {
  return (cleanAnswers(answers) as AnswerMap | undefined) ?? {};
}

/** Deterministic JSON with sorted object keys, dropping reserved `_`-prefixed keys. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const body = Object.entries(value as Record<string, unknown>)
      .filter(([k]) => !k.startsWith('_'))
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`)
      .join(',');
    return `{${body}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/**
 * A stable, order-insensitive fingerprint of a section's *meaningful* answers,
 * used to detect unsaved edits. Cleans empties (so seeding ''/null is a no-op),
 * sorts object keys (two equal answer sets built in a different insertion order
 * compare equal), and drops reserved `_`-prefixed keys (e.g. `_ref`).
 */
export function sectionAnswersFingerprint(answers: AnswerMap): string {
  return stableStringify(cleanSectionAnswers(answers ?? {}));
}

/** Whether a single field's current value counts as "filled" for a required check. */
function fieldHasValue(field: WizardField, value: unknown): boolean {
  // A required yes/no (radio boolean) is "answered" once it's either Yes or No; a
  // plain checkbox (consent-style) must be ticked. Everything else just needs a
  // non-empty value (cleanAnswers prunes ''/null/empty objects/arrays but keeps 0/false).
  if (field.type === 'boolean') {
    return field.display === 'radio' ? typeof value === 'boolean' : value === true;
  }
  // A multiselect is "answered" once at least one option is ticked.
  if (field.type === 'multiselect') {
    return Array.isArray(value) && value.length > 0;
  }
  return cleanAnswers(value) !== undefined;
}

/**
 * Every visible required field in a flat list (same answer scope) is filled. A
 * field whose value is auto-populated from ANOTHER section (`lockedFields`,
 * rendered read-only here — e.g. Expenses "Property maintenance" derived from
 * Investment Properties) is skipped: the user can't edit it, so it must never block
 * "Save Details" whether or not its source yielded a value.
 */
function requiredFieldsFilled(
  fields: WizardField[],
  answers: AnswerMap,
  revealed: Set<string>,
  lockedFields?: ReadonlySet<string>,
): boolean {
  return visibleFields(fields, answers, revealed)
    .filter((f) => f.required && !lockedFields?.has(f.id))
    .every((f) => fieldHasValue(f, answers[f.id]));
}

/**
 * Whether every visible required (`*`) field in a single group is filled. A
 * gated group whose gate is closed ("No") demands nothing, so it counts as
 * complete. Drives the per-group "Save Details" buttons (e.g. Dependants /
 * Family); also reused by {@link isSectionComplete}.
 */
export function isGroupComplete(
  group: WizardGroup,
  answers: AnswerMap,
  lockedFields?: ReadonlySet<string>,
): boolean {
  if (group.gate) {
    // A mandatory yes/no group gate must be answered before the group counts as
    // complete; once answered "No" (closed) it demands nothing further. An
    // `optional` gate is exempt — left unanswered it behaves as a closed gate
    // (nothing required), so it never blocks "Save Details".
    if (!group.gate.optional && !isGateAnswered(group.gate, answers)) return false;
    if (!gateState(group.gate, answers).open) return true;
  }
  if (group.repeating) {
    const items = Array.isArray(answers[group.id]) ? (answers[group.id] as AnswerMap[]) : [{}];
    return items.every(
      (item) =>
        requiredFieldsFilled(group.fields, item, new Set(['*']), lockedFields) &&
        subGroupsComplete(group, item, lockedFields),
    );
  }
  const groupAnswers = (answers[group.id] as AnswerMap) ?? {};
  return (
    requiredFieldsFilled(group.fields, groupAnswers, new Set(['*']), lockedFields) &&
    subGroupsComplete(group, groupAnswers, lockedFields)
  );
}

/**
 * Every nested sub-group (see {@link WizardGroup.subGroups}) whose `showIf`
 * is satisfied against this item is itself complete. A sub-group is scoped
 * to the parent item: `isGroupComplete`'s own `answers[group.id]`/gate lookup
 * works unchanged when `answers` is the item rather than the section.
 */
function subGroupsComplete(
  group: WizardGroup,
  itemAnswers: AnswerMap,
  lockedFields?: ReadonlySet<string>,
): boolean {
  return (group.subGroups ?? []).every(
    (sub) => !showIfSatisfied(sub, itemAnswers) || isGroupComplete(sub, itemAnswers, lockedFields),
  );
}

/**
 * Whether the section-LEVEL required fields are filled (ignoring groups). A
 * closed section gate demands nothing. Used so a per-card Save (e.g. Property's
 * investment-property card) also requires the section-level fields — like
 * Property's "Your living situation" — before it enables.
 */
export function areSectionFieldsComplete(
  section: WizardSection,
  answers: AnswerMap,
  lockedFields?: ReadonlySet<string>,
): boolean {
  const gate = gateState(section.gate, answers);
  if (section.gate && !gate.open) return true;
  return requiredFieldsFilled(section.fields ?? [], answers, gate.revealed, lockedFields);
}

/**
 * Whether every visible required (`*`) field in a section is filled — drives
 * the "Save Details" disabled state. Mirrors the renderer's visibility rules
 * (section/group gates, showIf, enum followUps, repeating groups, PER_CLIENT
 * blocks) so we only demand what the user can actually see. Section/group
 * gates themselves are not "*" fields and are not required here. CHAT-mode
 * sections complete through the chat flow and are not validated by this.
 */
export function isSectionComplete(
  section: WizardSection,
  answers: AnswerMap,
  clientIds: string[],
  lockedFields?: ReadonlySet<string>,
): boolean {
  const sectionGate = gateState(section.gate, answers);

  // A mandatory yes/no (or multiselect) section gate must be explicitly answered
  // before the section can be marked done — boolean needs Yes or No, multiselect
  // needs a selection. Once answered "No"/closed it demands nothing further. An
  // `optional` gate is exempt (an unanswered optional gate reads as closed).
  if (section.gate && !section.gate.optional && !isGateAnswered(section.gate, answers)) {
    return false;
  }

  // PER_CLIENT: one block of the section's fields per client; all must be filled.
  if (section.layout === 'PER_CLIENT') {
    const byClient = (answers.byClient as Record<string, AnswerMap>) ?? {};
    return clientIds.every((id) =>
      requiredFieldsFilled(section.fields ?? [], byClient[id] ?? {}, new Set(['*']), lockedFields),
    );
  }

  // Section-level fields (only enforced when the gate is open or absent).
  if (
    (sectionGate.open || !section.gate) &&
    !requiredFieldsFilled(section.fields ?? [], answers, sectionGate.revealed, lockedFields)
  ) {
    return false;
  }

  // Groups (repeating cards or single field grids), honouring reveal + gate.
  for (const group of section.groups ?? []) {
    if (!isGroupRevealed(group, sectionGate) && !group.alwaysVisible) continue;
    if (!isGroupComplete(group, answers, lockedFields)) return false;
  }

  return true;
}

/**
 * The section to open when resuming a draft: the first section in schema order
 * that isn't COMPLETE (so a half-finished or untouched section), falling back to
 * the first section when everything is complete. Returns null for an empty schema.
 */
export function resumeSectionId(
  sections: WizardSection[],
  sectionStatus: Record<string, string>,
): string | null {
  const firstIncomplete = sections.find((s) => sectionStatus[s.id] !== 'COMPLETE');
  return firstIncomplete?.id ?? sections[0]?.id ?? null;
}

/** The next section after `currentId` in schema order (wrap to first incomplete), or null. */
export function nextSectionId(
  sections: WizardSection[],
  currentId: string,
  sectionStatus: Record<string, string>,
): string | null {
  const ids = sections.map((s) => s.id);
  const start = ids.indexOf(currentId);
  for (let offset = 1; offset <= ids.length; offset++) {
    const candidate = ids[(start + offset) % ids.length];
    if (candidate === currentId) break;
    if (sectionStatus[candidate] !== 'COMPLETE') return candidate;
  }
  return null;
}
